import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { StaticQuery, graphql } from 'gatsby';
import { injectIntl, intlShape } from 'react-intl';
import Transition from 'react-transition-group/Transition';

import { COLOR_BLACK, COLOR_OUTLINE_BLUE, COLOR_WHITE } from '../constants/colors';
import { COOKIE_CONSENT_DECIDED_EVENT } from '../constants/cookies';
import { fadeIn } from '../constants/keyframes';
import { breakpoints } from '../constants/mediaqueries';
import { SPACING_NAV_SIDE_DESKTOP, SPACING_SIDE_MOBILE, SPACING_SIDE_MOBILE_HALF } from '../constants/spacings';
import { fontStyles } from '../constants/styles';
import { TRANSITION_TIME_TOTAL } from '../constants/transitions';
import { CONSENT_UNDECIDED, getMarketingConsentState } from '../helpers/analytics';
import { dismissAnnouncement, isAnnouncementDismissed, isAnnouncementLive } from '../helpers/announcement';
import { getNavOpen } from '../state/reducers';
import { fluidPropTypes } from '../types';

import CloseSymbol from './CloseSymbol';
import ExternalLink from './ExternalLink';
import InvisibleButton from './InvisibleButton';
import MichelbergerImage from './MichelbergerImage';


const ESCAPE_KEY_CODE = 27;
const TITLE_ID = 'announcement-title';

/*
 * Bottom-right card, same box as the cookie dialog (1px black border, white,
 * 1.25rem padding) and the same right-edge offsets as the header's booking
 * link. Fades in with the fadeIn keyframe while entering, like the splash;
 * afterwards its opacity follows the navigation (hidden while the nav is
 * open, as the homepage label does) and the exit state.
 *
 * z-index 2 keeps it under the homepage splash (z-index 10, opaque), which
 * is what sequences "prism first" on a first visit: the card, like the
 * cookie dialog, is only revealed once the splash has faded.
 */
const Card = styled.section`
  position: fixed;
  z-index: 2;
  right: ${SPACING_SIDE_MOBILE_HALF};
  bottom: ${SPACING_SIDE_MOBILE_HALF};
  max-width: calc(100% - ${SPACING_SIDE_MOBILE});
  padding: ${SPACING_SIDE_MOBILE_HALF};
  padding-right: 2.5rem;

  border: 1px solid ${COLOR_BLACK};
  background: ${COLOR_WHITE};
  color: ${COLOR_BLACK};
  ${fontStyles.uppercase}

  opacity: ${({ navOpen, transition }) => (navOpen || transition === 'exiting' ? 0 : 1)};
  pointer-events: ${({ navOpen }) => (navOpen ? 'none' : 'auto')};
  transition: opacity 0.5s ease-out;

  animation-name: ${({ transition }) => (transition === 'entering' ? fadeIn : 'none')};
  animation-duration: ${TRANSITION_TIME_TOTAL}ms;
  animation-timing-function: ease-in;
  animation-fill-mode: forwards;

  @media ${breakpoints.l} {
    right: ${SPACING_NAV_SIDE_DESKTOP};
    bottom: ${SPACING_NAV_SIDE_DESKTOP};
    max-width: 20rem;
  }
`;

// reset.css flattens every heading to weight 400. The site ships no bold
// face, so this is the browser's synthesized bold from the regular outlines;
// the hotel asked for it on the title and nowhere else in the card.
const Title = styled.h2`
  ${fontStyles.uppercase}
  margin: 0 0 0.625rem;
  font-weight: 700;
`;

// Plain text on purpose, not markdown: a date line such as "10. & 11. Oktober"
// starts with what markdown reads as a numbered list and loses its "10.".
// pre-line turns an Enter in the Dato editor into the line break the hotel
// expects; the link has its own field.
const Text = styled.p`
  margin: 0 0 0.625rem;
  white-space: pre-line;
`;

const ImageWrapper = styled.div`
  margin-bottom: ${SPACING_SIDE_MOBILE_HALF};
`;

const CloseButton = styled(InvisibleButton)`
  position: absolute;
  top: ${SPACING_SIDE_MOBILE_HALF};
  right: ${SPACING_SIDE_MOBILE_HALF};
  width: 1rem;
  height: 1rem;

  &:focus {
    outline: 2px solid ${COLOR_OUTLINE_BLUE};
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const isConsentDecided = () => getMarketingConsentState() !== CONSENT_UNDECIDED;

export class AnnouncementCard extends Component {
  constructor(props) {
    super(props);

    // Starts hidden and decides in componentDidMount, so the server-rendered
    // markup and the first client render agree: the consent cookie and the
    // dismissal in localStorage are only known in the browser.
    this.state = { consentDecided: false, dismissed: true };

    this.handleConsentDecided = this.handleConsentDecided.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }

  componentDidMount() {
    const { campaign, now } = this.props;

    this.setState({
      consentDecided: isConsentDecided(),
      dismissed: isAnnouncementDismissed(campaign, now()),
    });
    document.body.addEventListener('keyup', this.handleKeyUp, false);
    window.addEventListener(COOKIE_CONSENT_DECIDED_EVENT, this.handleConsentDecided);
  }

  componentWillUnmount() {
    document.body.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener(COOKIE_CONSENT_DECIDED_EVENT, this.handleConsentDecided);
  }

  isOpen() {
    const { consentDecided, dismissed } = this.state;

    return consentDecided && !dismissed;
  }

  handleConsentDecided() {
    this.setState({ consentDecided: isConsentDecided() });
  }

  handleDismiss() {
    const { campaign, now } = this.props;

    dismissAnnouncement(campaign, now());
    this.setState({ dismissed: true });
  }

  handleKeyUp(event) {
    if (event.keyCode === ESCAPE_KEY_CODE && this.isOpen()) {
      this.handleDismiss();
    }
  }

  render() {
    const {
      closeLabel, image, linkLabel, linkUrl, navOpen, text, title,
    } = this.props;

    return (
      <Transition appear enter in={this.isOpen()} mountOnEnter timeout={TRANSITION_TIME_TOTAL} unmountOnExit>
        {(state) => (
          <Card aria-labelledby={TITLE_ID} navOpen={navOpen} role="region" transition={state}>
            <CloseButton aria-label={closeLabel} onClick={this.handleDismiss} type="button">
              <CloseSymbol />
            </CloseButton>
            <If condition={!!image}>
              <ImageWrapper>
                <MichelbergerImage image={image} />
              </ImageWrapper>
            </If>
            <Title id={TITLE_ID}>{title}</Title>
            <Text>{text}</Text>
            <ExternalLink href={linkUrl}>{linkLabel}</ExternalLink>
          </Card>
        )}
      </Transition>
    );
  }
}

AnnouncementCard.propTypes = {
  campaign: PropTypes.string.isRequired,
  closeLabel: PropTypes.string.isRequired,
  image: PropTypes.shape({
    alt: PropTypes.string,
    mimeType: PropTypes.string,
    url: PropTypes.string,
    fluid: fluidPropTypes,
  }),
  linkLabel: PropTypes.string.isRequired,
  linkUrl: PropTypes.string.isRequired,
  navOpen: PropTypes.bool,
  now: PropTypes.func,
  text: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

AnnouncementCard.defaultProps = {
  image: null,
  navOpen: false,
  now: Date.now,
};

// The record for the current locale, or null when the model has no record
// yet. Unlike getSingletonForLang this does not throw: a missing
// announcement must never take a page down.
const findAnnouncement = (data, langKey) => {
  const edges = (data && data.allDatoCmsAnnouncement && data.allDatoCmsAnnouncement.edges) || [];
  const edge = edges.find((candidate) => candidate.node.locale === langKey);

  return edge ? edge.node : null;
};

export const AnnouncementComponent = ({ intl, langKey, navOpen }) => (
  <StaticQuery
    query={graphql`
      query AnnouncementQuery {
        allDatoCmsAnnouncement {
          edges {
            node {
              locale
              active
              campaign
              hideFrom
              title
              text
              linkLabel
              linkUrl
              image {
                alt
                mimeType
                url
                fluid(maxWidth: 800, imgixParams: { auto: "format" }) {
                  ...GatsbyDatoCmsFluid
                }
              }
            }
          }
        }
      }
    `}
    render={(data) => {
      const announcement = findAnnouncement(data, langKey);

      if (!announcement || !isAnnouncementLive(announcement)) {
        return null;
      }

      return (
        <AnnouncementCard
          campaign={announcement.campaign}
          closeLabel={intl.formatMessage({ id: 'global.close' })}
          image={announcement.image}
          linkLabel={announcement.linkLabel}
          linkUrl={announcement.linkUrl}
          navOpen={navOpen}
          text={announcement.text}
          title={announcement.title}
        />
      );
    }}
  />
);

AnnouncementComponent.propTypes = {
  intl: intlShape.isRequired,
  langKey: PropTypes.string.isRequired,
  navOpen: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  navOpen: getNavOpen(state),
});

export default injectIntl(connect(mapStateToProps)(AnnouncementComponent));
